import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

import type {
    DeclarationStatus,
    Language,
    LocalizedText,
    PrincipleGroup,
    Scope,
    VerificationLevel,
} from '@/types'

const STORAGE_KEY = 'fip-dashboard.language'

type Dictionary = Record<string, LocalizedText>

const strings: Dictionary = {
    'app.title': { en: 'FIP Dashboard', cs: 'Dashboard FIP' },
    'app.owner': { en: 'NRP · EOSC CZ', cs: 'NRP · EOSC CZ' },
    'app.subtitle': {
        en: 'FAIR enabling resources and implementation profiles of the Czech National Data Infrastructure',
        cs: 'Zdroje umožňující FAIR a profily implementace Národní datové infrastruktury ČR',
    },
    'footer.funding': {
        en: 'Co-funded by the European Union · Ministry of Education, Youth and Sports',
        cs: 'Spolufinancováno Evropskou unií · Ministerstvo školství, mládeže a tělovýchovy',
    },
    'nav.overview': { en: 'Overview', cs: 'Přehled' },
    'nav.resources': { en: 'Resources', cs: 'Zdroje' },
    'nav.profiles': { en: 'Profiles', cs: 'Profily' },
    'nav.matrix': { en: 'Convergence matrix', cs: 'Matice konvergence' },
    'nav.about': { en: 'About', cs: 'O aplikaci' },

    'common.loading': { en: 'Loading…', cs: 'Načítání…' },
    'common.error': { en: 'Could not load data', cs: 'Data se nepodařilo načíst' },
    'common.errorHint': {
        en: 'Is the backend running on port 8000?',
        cs: 'Běží backend na portu 8000?',
    },
    'common.retry': { en: 'Try again', cs: 'Zkusit znovu' },
    'common.search': { en: 'Search', cs: 'Hledat' },
    'common.searchPlaceholder': {
        en: 'Search resources, standards, services…',
        cs: 'Hledat zdroje, standardy, služby…',
    },
    'common.filters': { en: 'Filters', cs: 'Filtry' },
    'common.clear': { en: 'Clear filters', cs: 'Zrušit filtry' },
    'common.noResults': { en: 'Nothing matches these filters.', cs: 'Filtrům nic neodpovídá.' },
    'common.website': { en: 'Website', cs: 'Webová stránka' },
    'common.back': { en: 'Back', cs: 'Zpět' },
    'common.notFound': { en: 'Not found', cs: 'Nenalezeno' },
    'common.demoData': { en: 'Demonstration data', cs: 'Demonstrační data' },
    'common.demoBadge': { en: 'Demo', cs: 'Ukázka' },
    'common.demoNotice': {
        en: 'This dashboard contains example data only and currently serves for demonstration and feedback purposes.',
        cs: 'Tento dashboard obsahuje pouze ukázková data a v současnosti slouží k demonstraci a sběru zpětné vazby.',
    },

    'overview.heroTitle': {
        en: 'FAIR enabling resources of the Czech national infrastructure',
        cs: 'Zdroje umožňující FAIR v české národní infrastruktuře',
    },
    'overview.browseResources': { en: 'Browse resources', cs: 'Procházet zdroje' },
    'overview.author': { en: 'Author in FIP Wizard', cs: 'Tvořit ve FIP Wizardu' },
    'overview.authorHint': {
        en: 'FIP Wizard is the recommended tool for authoring resources and FIPs',
        cs: 'FIP Wizard je doporučený nástroj pro tvorbu zdrojů a profilů FIP',
    },
    'overview.statusesHint': {
        en: 'Not every choice is already in production — profiles also declare what is planned, considered or on its way out.',
        cs: 'Ne každá volba je již v provozu — profily deklarují i to, co je plánováno, zvažováno nebo bude nahrazeno.',
    },
    'overview.lead': {
        en: 'Browse what Czech research communities use to make their data FAIR — and where their choices converge.',
        cs: 'Prohlédněte si, co české výzkumné komunity používají, aby jejich data byla FAIR — a kde se jejich volby setkávají.',
    },
    'overview.stat.fips': { en: 'Implementation profiles', cs: 'Profily implementace' },
    'overview.stat.resources': { en: 'Enabling resources', cs: 'Zdroje umožňující FAIR' },
    'overview.stat.organizations': { en: 'Organisations', cs: 'Organizace' },
    'overview.stat.declarations': { en: 'Declarations', cs: 'Deklarace' },
    'overview.coverage': { en: 'Coverage of the FAIR principles', cs: 'Pokrytí principů FAIR' },
    'overview.coverageHint': {
        en: 'How many of the profiles declare at least one resource for each principle.',
        cs: 'Kolik profilů deklaruje alespoň jeden zdroj pro každý princip.',
    },
    'overview.top': { en: 'Most widely shared resources', cs: 'Nejsdílenější zdroje' },
    'overview.topHint': {
        en: 'Resources declared by several communities are the natural building blocks of the national infrastructure.',
        cs: 'Zdroje deklarované více komunitami jsou přirozenými stavebními kameny národní infrastruktury.',
    },
    'overview.profiles': { en: 'Implementation profiles', cs: 'Profily implementace' },
    'overview.types': { en: 'Resources by category', cs: 'Zdroje podle kategorií' },
    'overview.statuses': { en: 'Declarations by status', cs: 'Deklarace podle stavu' },

    'resources.title': { en: 'FAIR enabling resources', cs: 'Zdroje umožňující FAIR' },
    'resources.lead': {
        en: 'Standards, services, policies and tools that communities declare in their profiles.',
        cs: 'Standardy, služby, politiky a nástroje, které komunity deklarují ve svých profilech.',
    },
    'resources.count': { en: 'resources', cs: 'zdrojů' },
    'resources.filter.type': { en: 'Category', cs: 'Kategorie' },
    'resources.filter.scope': { en: 'Scope', cs: 'Působnost' },
    'resources.filter.principle': { en: 'FAIR principle', cs: 'Princip FAIR' },
    'resources.filter.fip': { en: 'Declared in profile', cs: 'Deklarováno v profilu' },

    'resource.provider': { en: 'Provider', cs: 'Poskytovatel' },
    'resource.scope': { en: 'Scope', cs: 'Působnost' },
    'resource.category': { en: 'Category', cs: 'Kategorie' },
    'resource.principles': { en: 'Supports principles', cs: 'Podporuje principy' },
    'resource.declaredBy': { en: 'Declared by', cs: 'Deklarováno v' },
    'resource.tags': { en: 'Tags', cs: 'Štítky' },
    'resource.identifier': { en: 'Identifier', cs: 'Identifikátor' },
    'resource.noUsage': {
        en: 'No community in this dataset declares this resource yet.',
        cs: 'Tento zdroj zatím nedeklaruje žádná komunita v této datové sadě.',
    },
    'resource.usedBy': { en: 'profiles', cs: 'profilů' },

    'verification.title': { en: 'Verification', cs: 'Ověření' },
    'verification.by.verified': { en: 'Verified by {name}', cs: 'Ověřeno: {name}' },
    'verification.by.under-review': {
        en: 'Under review by {name}',
        cs: 'Posuzuje: {name}',
    },
    'verification.by.self-declared': {
        en: 'Self-declared by {name}',
        cs: 'Vlastní prohlášení: {name}',
    },
    'verification.record': { en: 'Verification record', cs: 'Záznam o ověření' },
    'verification.none': {
        en: 'No organisation has reviewed this record.',
        cs: 'Tento záznam zatím nikdo neposoudil.',
    },
    'verification.noneShort': { en: 'Not reviewed', cs: 'Neposouzeno' },
    'verification.hint': {
        en: 'Who checked this record, when, and how thoroughly.',
        cs: 'Kdo záznam posoudil, kdy a jak důkladně.',
    },
    'verification.filter': { en: 'Verification', cs: 'Ověření' },
    'verification.verifiedOnly': { en: 'Verified only', cs: 'Pouze ověřené' },
    'verification.unverifiedOnly': { en: 'Not verified', cs: 'Neověřené' },
    'verification.stat': { en: 'Verified resources', cs: 'Ověřených zdrojů' },
    'verification.verifiers': { en: 'Verifying organisations', cs: 'Ověřující organizace' },
    'verification.verifiersHint': {
        en: 'How many resource and profile records each organisation has reviewed.',
        cs: 'Kolik záznamů zdrojů a profilů každá organizace posoudila.',
    },

    'fips.title': { en: 'FAIR Implementation Profiles', cs: 'Profily implementace FAIR' },
    'fips.lead': {
        en: 'Each profile is one community declaring which resources it uses for each FAIR principle.',
        cs: 'Každý profil je deklarace jedné komunity o tom, které zdroje používá pro jednotlivé principy FAIR.',
    },
    'fip.community': { en: 'Community', cs: 'Komunita' },
    'fip.domain': { en: 'Domain', cs: 'Doména' },
    'fip.version': { en: 'Version', cs: 'Verze' },
    'fip.updated': { en: 'Updated', cs: 'Aktualizováno' },
    'fip.declarations': { en: 'Declarations', cs: 'Deklarace' },
    'fip.principlesCovered': { en: 'Principles covered', cs: 'Pokrytých principů' },
    'fip.resourcesUsed': { en: 'Resources declared', cs: 'Deklarovaných zdrojů' },
    'fip.openProfile': { en: 'Open profile', cs: 'Otevřít profil' },
    'fip.gap': {
        en: 'No resource declared for this principle.',
        cs: 'Pro tento princip není deklarován žádný zdroj.',
    },
    'fip.status.published': { en: 'Published', cs: 'Publikováno' },
    'fip.status.draft': { en: 'Draft', cs: 'Pracovní verze' },

    'matrix.title': { en: 'FAIR convergence matrix', cs: 'Matice konvergence FAIR' },
    'matrix.lead': {
        en: 'Principles against profiles: where communities already agree, and where they do not.',
        cs: 'Principy proti profilům: kde se komunity již shodují a kde nikoli.',
    },
    'matrix.select': { en: 'Profiles shown', cs: 'Zobrazené profily' },
    'matrix.shared': { en: 'Shared across profiles', cs: 'Sdíleno napříč profily' },
    'matrix.sharedHint': {
        en: 'Resources declared by more than one community.',
        cs: 'Zdroje deklarované více než jednou komunitou.',
    },
    'matrix.highlight': { en: 'Highlight shared choices', cs: 'Zvýraznit sdílené volby' },
    'matrix.legend': { en: 'Status legend', cs: 'Legenda stavů' },

    'about.title': { en: 'About this dashboard', cs: 'O tomto dashboardu' },
    'about.whatTitle': { en: 'What is a FIP?', cs: 'Co je FIP?' },
    'about.what': {
        en: 'A FAIR Implementation Profile is a list of the technology choices a research community makes to implement each of the FAIR guiding principles. Communities that publish their profiles can reuse each other’s choices instead of solving the same problems again.',
        cs: 'Profil implementace FAIR (FIP) je seznam technologických voleb, které výzkumná komunita činí při naplňování jednotlivých principů FAIR. Komunity, které své profily zveřejní, mohou volby přebírat od sebe navzájem místo opakovaného řešení stejných problémů.',
    },
    'about.authoringTitle': {
        en: 'Authoring resources and FIPs',
        cs: 'Tvorba zdrojů a profilů FIP',
    },
    'about.authoring': {
        en: 'This dashboard only presents FAIR enabling resources and FAIR Implementation Profiles. To describe a resource or to author your community’s FIP, we recommend the FIP Wizard.',
        cs: 'Tento dashboard zdroje umožňující FAIR a profily implementace FAIR pouze zobrazuje. K popisu zdroje nebo k sestavení profilu FIP vaší komunity doporučujeme FIP Wizard.',
    },
    'about.authoringCta': { en: 'Open FIP Wizard', cs: 'Otevřít FIP Wizard' },
    'about.dataTitle': { en: 'About the data', cs: 'O datech' },
    'about.apiTitle': { en: 'API', cs: 'API' },
    'about.api': {
        en: 'Everything shown here comes from the dashboard’s own JSON API. The interactive documentation is generated by FastAPI.',
        cs: 'Vše zde zobrazené pochází z vlastního JSON API dashboardu. Interaktivní dokumentaci generuje FastAPI.',
    },
    'about.sources': { en: 'Reference links', cs: 'Referenční odkazy' },
    'about.backend': { en: 'Backend status', cs: 'Stav backendu' },
}

const groupNames: Record<PrincipleGroup, LocalizedText> = {
    F: { en: 'Findable', cs: 'Nalezitelná' },
    A: { en: 'Accessible', cs: 'Dostupná' },
    I: { en: 'Interoperable', cs: 'Interoperabilní' },
    R: { en: 'Reusable', cs: 'Znovupoužitelná' },
}

const statusNames: Record<DeclarationStatus, LocalizedText> = {
    'in-use': { en: 'In use', cs: 'Používá se' },
    planned: { en: 'Planned', cs: 'Plánováno' },
    'under-consideration': { en: 'Under consideration', cs: 'Zvažuje se' },
    'to-be-replaced': { en: 'To be replaced', cs: 'Bude nahrazeno' },
}

const verificationNames: Record<VerificationLevel, LocalizedText> = {
    verified: { en: 'Verified', cs: 'Ověřeno' },
    'self-declared': { en: 'Self-declared', cs: 'Vlastní prohlášení' },
    'under-review': { en: 'Under review', cs: 'V posuzování' },
}

const scopeNames: Record<Scope, LocalizedText> = {
    global: { en: 'Global', cs: 'Globální' },
    european: { en: 'European', cs: 'Evropský' },
    national: { en: 'National', cs: 'Národní' },
    institutional: { en: 'Institutional', cs: 'Institucionální' },
}

type I18nValue = {
    language: Language
    setLanguage: (language: Language) => void
    t: (key: string) => string
    localized: (text: LocalizedText | null | undefined) => string
    groupName: (group: PrincipleGroup) => string
    statusName: (status: DeclarationStatus) => string
    verificationName: (level: VerificationLevel) => string
    scopeName: (scope: Scope) => string
    formatDate: (value: string) => string
}

const I18nContext = createContext<I18nValue | null>(null)

function initialLanguage(): Language {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (stored === 'cs' || stored === 'en') {
            return stored
        }
    } catch {
        // Storage is unavailable (private mode); fall through to the default.
    }
    return navigator.language.startsWith('cs') ? 'cs' : 'en'
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<Language>(initialLanguage)

    const setLanguage = useCallback((next: Language) => {
        setLanguageState(next)
        document.documentElement.lang = next
        try {
            window.localStorage.setItem(STORAGE_KEY, next)
        } catch {
            // Remembering the choice is optional.
        }
    }, [])

    const value = useMemo<I18nValue>(() => {
        const pick = (text: LocalizedText | null | undefined) =>
            text ? (language === 'cs' ? text.cs : text.en) : ''
        return {
            language,
            setLanguage,
            t: (key: string) => pick(strings[key]) || key,
            localized: pick,
            groupName: (group: PrincipleGroup) => pick(groupNames[group]),
            statusName: (status: DeclarationStatus) => pick(statusNames[status]),
            verificationName: (level: VerificationLevel) => pick(verificationNames[level]),
            scopeName: (scope: Scope) => pick(scopeNames[scope]),
            formatDate: (value: string) =>
                new Date(value).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-GB', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                }),
        }
    }, [language, setLanguage])

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
    const value = useContext(I18nContext)
    if (!value) {
        throw new Error('useI18n must be used inside an I18nProvider')
    }
    return value
}
